/**
 * @fileoverview TEMPORARY bridge from backend capability grants to this app's privilege keys.
 * @description The app gates its routes and menus on fine-grained keys (`sales:pipeline:read`,
 * `banking:loans:read`, …) and runs its sales hierarchy rules on a role name ("Sales Head"), but
 * GET /authorization/me/capabilities returns only coarse permission codes (USER.INVITE,
 * DASHBOARD.READ, …) and a roleId, and a regular user can't read the role catalogue to turn that
 * id into a name. Every system role does hold a distinct set of codes (the backend's
 * SYSTEM_ROLE_PERMISSION_CATALOGUE), so this infers the role from the codes held and grants that
 * role the keys it had in this app's mock users. A few keys map one-to-one onto a code instead
 * (CODE_PRIVILEGES — e.g. `users:invite` follows USER.INVITE), so those follow the backend exactly
 * whatever the role; a few of those also wait for the other codes their page's requests need
 * (CODE_REQUIRED_PRIVILEGES). Custom roles, or anything outside those sets, resolve to no role. Delete this file once the backend serves role information or
 * app-level permission codes directly.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import type { CapabilityGrant } from '@/features/auth/services/capabilities.service'

export interface CapabilityAccess {
  activeRoleKey: string
  activeRoleName: string
  privileges: string[]
}

interface RoleProfile {
  key: string
  name: string
  privileges: readonly string[]
}

const SALES_VIEW_PRIVILEGES = [
  'sales:dashboard:view',
  'sales:clients:read',
  'sales:pipeline:read',
  'sales:performance:read',
  'sales:team:read',
  'sales:my-page:view',
] as const

const BANKING_PRIVILEGES = [
  'banking:overview:view',
  'banking:bank-details:view',
  'banking:membership:read',
  'banking:customer-base:read',
  'banking:deposits:read',
  'banking:loans:read',
  'banking:investments:read',
  'banking:asset-quality:read',
  'banking:profitability:read',
  'banking:capital-adequacy:read',
  'banking:branch-analytics:read',
  'banking:productivity:read',
  'banking:business-performance:read',
] as const

const SUPER_ADMIN: RoleProfile = {
  key: 'super-admin',
  name: 'Super Admin',
  privileges: [
    'users:read',
    'users:write',
    'users:delete',
    'users:invite',
    'roles:read',
    'roles:write',
    'roles:assign',
    'sessions:read',
    'sessions:revoke',
    'masters:read',
    'masters:write',
    'tenant:profile',
    // Only this role is given audit:logs:read: the Audit Log is a Super Admin page.
    'audit:logs:read',
    ...SALES_VIEW_PRIVILEGES,
    'sales:users:manage',
  ],
}

const SALES_HEAD: RoleProfile = {
  key: 'sales-head',
  name: 'Sales Head',
  privileges: [
    'users:read',
    'users:write',
    'users:invite',
    'roles:read',
    'roles:assign',
    'masters:read',
    'sessions:read',
    ...SALES_VIEW_PRIVILEGES,
    'sales:users:manage',
  ],
}

const SALES_MANAGER: RoleProfile = {
  key: 'sales-manager',
  name: 'Sales Manager',
  privileges: ['masters:read', ...SALES_VIEW_PRIVILEGES, 'sales:users:manage'],
}

// The backend's SALES_EXECUTIVE role.
const SALES_EXECUTIVE: RoleProfile = {
  key: 'sales-executive',
  name: 'Sales Executive',
  privileges: ['masters:read', ...SALES_VIEW_PRIVILEGES],
}

/**
 * App keys that follow a single backend permission code, whatever the role (subject to
 * CODE_REQUIRED_PRIVILEGES below).
 */
const CODE_PRIVILEGES: Readonly<Record<string, string>> = {
  'USER.INVITE': 'users:invite',
  'USER.READ': 'users:read',
  'USER.UPDATE': 'users:update',
  'SALES_CRM.PROSPECT.ASSIGN': 'sales:prospects:assign',
  'SALES_CRM.ORGANISATION_DETAILS.UPDATE': 'sales:organisation:update',
  'SALES_CRM.PROSPECT.UPDATE': 'sales:prospects:update',
  'SALES_CRM.PROSPECT.ARCHIVE': 'sales:prospects:archive',
  'SALES_CRM.PROSPECT.RESTORE': 'sales:prospects:restore',
  'SALES_CRM.PROSPECT.VIEW_PENDING': 'sales:prospects:view-pending',
  'SALES_CRM.TARGET.ASSIGN': 'sales:targets:assign',
  'APPROVAL.DECIDE': 'sales:approvals:decide',
  'AUTHORIZATION.PERMISSION.GRANT': 'capabilities:manage',
}

/**
 * App keys a user is given only while the backend also grants every code that the page's API calls
 * need, so the menu and route never offer a page whose requests the backend would refuse (e.g. the
 * Audit Log needs AUDIT.READ for GET /audit/events; Capabilities, which a user reaches with
 * AUTHORIZATION.PERMISSION.GRANT, also needs REVOKE to save a revoke and the role and permission
 * reads to load the matrix).
 */
const CODE_REQUIRED_PRIVILEGES: Readonly<Record<string, readonly string[]>> = {
  'audit:logs:read': ['AUDIT.READ'],
  'capabilities:manage': [
    'AUTHORIZATION.PERMISSION.REVOKE',
    'AUTHORIZATION.ROLE.READ',
    'AUTHORIZATION.PERMISSION.READ',
  ],
}

/**
 * @description Builds a bank role's profile: every banking dashboard page, but only when the role
 * may read dashboards or reports at all.
 * @param {string} key - The role key.
 * @param {string} name - The role's display name.
 * @param {ReadonlySet<string>} codes - The permission codes held.
 * @returns {RoleProfile} The bank role's profile.
 */
function bankRole(key: string, name: string, codes: ReadonlySet<string>): RoleProfile {
  const canViewDashboards = codes.has('DASHBOARD.READ') || codes.has('REPORT.READ')
  return { key, name, privileges: canViewDashboards ? BANKING_PRIVILEGES : [] }
}

/**
 * @description Infers a bank (EXTERNAL) role from its distinct permission set.
 * @param {ReadonlySet<string>} codes - The permission codes held.
 * @returns {RoleProfile | null} The bank role, or null when the codes match none.
 */
function inferBankRole(codes: ReadonlySet<string>): RoleProfile | null {
  const canExport = codes.has('REPORT.EXPORT')

  if (codes.has('USER.INVITE')) {
    return bankRole('bank-hr-head', 'Bank HR Head', codes)
  }

  if (codes.has('AUDIT.READ')) {
    return canExport
      ? bankRole('bank-board-director', 'Bank Board of Director', codes)
      : bankRole('bank-auditor', 'Bank Auditor', codes)
  }

  if (codes.has('USER.READ')) {
    return canExport
      ? bankRole('bank-regional-manager', 'Bank Regional Manager', codes)
      : bankRole('bank-branch-manager', 'Bank Branch Manager', codes)
  }

  if (codes.has('DATA_INGESTION.UPLOAD')) {
    return bankRole('bank-tech-admin', 'Bank Tech Admin', codes)
  }

  return null
}

/**
 * @description Infers the role from the permission codes held, most powerful first.
 * @param {ReadonlySet<string>} codes - The permission codes held.
 * @returns {RoleProfile | null} The inferred role, or null when the codes match none.
 */
function inferRole(codes: ReadonlySet<string>): RoleProfile | null {
  if (codes.has('AUTHORIZATION.RULE.UPDATE')) {
    return SUPER_ADMIN
  }

  if (codes.has('TENANT.CREATE')) {
    if (codes.has('USER.INVITE')) {
      return codes.has('REPORT.EXPORT') ? SALES_HEAD : SALES_MANAGER
    }
    return SALES_EXECUTIVE
  }

  if (codes.has('AUTHORIZATION.RULE.READ')) {
    // Platform Support: no screens in this app yet.
    return { key: 'platform-support', name: 'Platform Support', privileges: [] }
  }

  return inferBankRole(codes)
}

/**
 * @description Whether a grant is inside its validity window right now.
 * @param {CapabilityGrant} grant - The grant.
 * @param {number} now - The current time, in epoch ms.
 * @returns {boolean} True when the grant currently applies.
 */
function isGrantActive(grant: CapabilityGrant, now: number): boolean {
  const startsOk = !grant.validFrom || new Date(grant.validFrom).getTime() <= now
  const endsOk = !grant.validUntil || new Date(grant.validUntil).getTime() > now
  return startsOk && endsOk
}

/**
 * @description Resolves the signed-in user's role and app privilege keys from their grants.
 * @param {CapabilityGrant[]} grants - Every capability grant.
 * @param {number} [now] - The current time, in epoch ms (injectable for tests).
 * @returns {CapabilityAccess} The inferred role and its privilege keys.
 */
export function resolveCapabilityAccess(
  grants: CapabilityGrant[],
  now: number = Date.now(),
): CapabilityAccess {
  const codes = new Set(
    grants.filter((grant) => isGrantActive(grant, now)).map((grant) => grant.permissionCode),
  )
  const role = inferRole(codes)
  const codePrivileges = Object.entries(CODE_PRIVILEGES)
    .filter(([code]) => codes.has(code))
    .map(([, privilege]) => privilege)

  return {
    activeRoleKey: role?.key ?? '',
    activeRoleName: role?.name ?? '',
    privileges: [...new Set([...(role?.privileges ?? []), ...codePrivileges])].filter(
      (privilege) => {
        const requiredCodes = CODE_REQUIRED_PRIVILEGES[privilege] ?? []
        return requiredCodes.every((code) => codes.has(code))
      },
    ),
  }
}
